import { useState } from 'react';
import { useAppContext, RESET_STATE } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../utils/urlState';
import { saveDesign } from '../api/designAPI';
import PlantShoppingList from '../components/PlantShoppingList';
import GardenShoppingList from '../components/GardenShoppingList';
import { Box, Text, Group, Button, Paper, ScrollArea, Stack, Modal } from '@mantine/core';
import { colors, fontSize } from '../constants';

const SummaryTwoMobile = () => {
	const navigate = useNavigate();
	const { navigateWithParams } = useUrlState();
	const { state, dispatch } = useAppContext();
	const {
		designName,
		zone,
		style,
		sunlight,
		windows,
		facade,
		houseSize,
		shape,
		bedSize,
		bedConfig,
		chosenPlants,
		designId,
	} = state.currentDesign;
	const [showModal, setShowModal] = useState(false);

	// Helper function to humanize values
	const humanizeValue = (value) => {
		if (!value) return value;
		return value
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};

	const handleBack = () => {
		navigateWithParams('/summary-one');
	};

	// Save Design
	const handleSaveDesign = async () => {
		try {
			await saveDesign({
				userId: state.user.id,
				designName,
				zone,
				style,
				sunlight,
				windows,
				facade,
				houseSize,
				shape,
				bedSize,
				bedConfig,
				chosenPlants,
				designId,
			});
			console.log('Design saved successfully!');
			setShowModal(true);
		} catch (error) {
			console.error('Error saving design:', error);
		}
	};

	// Navigate to Printable Summary Page
	const handleGeneratePDF = () => {
		navigateWithParams('/printable-summary');
	};

	// Close modal and reset state
	const handleCloseModal = () => {
		setShowModal(false);
		dispatch({ type: RESET_STATE });
		navigateWithParams('/');
	};

	return (
		<Box style={{ 
			height: '100vh', 
			width: '100vw',
			display: 'flex', 
			flexDirection: 'column',
			backgroundColor: colors.white,
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			overflow: 'hidden',
			boxSizing: 'border-box'
		}}>
			{/* Header */}
			<Box style={{
				backgroundColor: colors.backgroundLight,
				height: '120px',
				padding: '10px 10px',
				zIndex: 10,
				position: 'relative',
				borderBottom: `12px solid ${colors.white}`,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-end',
				alignItems: 'center'
			}}>
				<Text style={{
					color: colors.white,
					fontSize: fontSize.xxxl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Summary
				</Text>
			</Box>

			{/* Navigation Bar */}
			<Group justify="space-between" align="center" style={{ 
				padding: '2.5vh 5vw',
				zIndex: 10,
				position: 'relative',
				height: '10.5vh',
				flexShrink: 0,
        backgroundColor: colors.mobileBackground,
			}}>
				<Button
					variant="filled"
					onClick={handleBack}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							fontWeight: 'bold',
							minHeight: '44px',
							fontSize: fontSize.lg,
						}
					}}
				>
					Back
				</Button>
				
				<Text style={{
					color: colors.white,
					fontSize: fontSize.xl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Shopping List
				</Text>
				
				{/* Empty space to maintain layout */}
				<Box style={{ width: '80px' }} />
			</Group>

			{/* Main Content Area */}
			<Box style={{ 
				flex: 1,
				minHeight: 0,
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
				{/* Shopping List Content */}
				<ScrollArea style={{ flex: 1, minHeight: 0 }}>
					<Stack gap="5px" style={{ paddingBottom: '20px' }}>
						{/* Your Chosen Plants */}
						<Paper style={{
							backgroundColor: colors.white,
							padding: '15px',
						}}>
							<Text style={{
								fontSize: fontSize.lg,
								fontWeight: 'bold',
								marginBottom: '15px',
								color: colors.text
							}}>
								Your Chosen Plants
							</Text>
							<Box style={{  }}>
								<PlantShoppingList plants={chosenPlants} />
							</Box>
						</Paper>

						{/* Additional Recommended Materials */}
						<Paper style={{
							backgroundColor: colors.white,
							padding: '15px',
							borderRadius: '10px',
							boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
						}}>
							<Text style={{
								fontSize: fontSize.lg,
								fontWeight: 'bold',
								marginBottom: '15px',
								color: colors.text
							}}>
								Additional Recommended Materials
							</Text>
							<Box style={{ overflowX: 'auto' }}>
								<GardenShoppingList />
							</Box>
						</Paper>

						{/* Design Summary */}
						<Paper style={{
							backgroundColor: colors.white,
							padding: '15px',
							borderRadius: '10px',
							boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
						}}>
							<Text style={{
								fontSize: fontSize.lg,
								fontWeight: 'bold',
								marginBottom: '15px',
								color: colors.text
							}}>
								Design Summary
							</Text>
							<Group align="flex-start" gap="0px">
								<Stack gap="4px" style={{ flex: 1 }}>
									<Text style={{ fontSize: fontSize.base, color: colors.text, textAlign: 'left' }}>
										<strong>Name:</strong> {designName}
									</Text>
									<Text style={{ fontSize: fontSize.base, color: colors.text, textAlign: 'left' }}>
										<strong>Zone:</strong> {zone}
									</Text>
									<Text style={{ fontSize: fontSize.base, color: colors.text, textAlign: 'left' }}>
										<strong>Sunlight:</strong> {humanizeValue(sunlight)}
									</Text>
								</Stack>
								<Stack gap="4px" style={{ flex: 1 }}>
									<Text style={{ fontSize: fontSize.base, color: colors.text, textAlign: 'left' }}>
										<strong>Style:</strong> {humanizeValue(style)}
									</Text>
									<Text style={{ fontSize: fontSize.base, color: colors.text, textAlign: 'left' }}>
										<strong>Bed Size:</strong> {bedSize}
									</Text>
								</Stack>
							</Group>
						</Paper>
					</Stack>
				</ScrollArea>
			</Box>

			{/* Sticky Footer with Action Buttons */}
			<Box style={{
				position: 'sticky',
				bottom: 0,
				left: 0,
				right: 0,
				backgroundColor: colors.mobileBackground,
        height: '13vh',
				padding: '20px 20px',
				borderTop: `2px solid ${colors.buttonColor}`,
				zIndex: 20,
				boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
			}}>
				<Group justify="space-between" align="center" gap="10px">
					<Button
						variant="filled"
						onClick={handleSaveDesign}
						styles={{
							root: {
								backgroundColor: colors.buttonColor,
								color: colors.white,
								border: 'none',
								borderRadius: '8px',
								padding: '12px 20px',
								fontWeight: 'bold',
								minHeight: '48px',
								fontSize: fontSize.xl,
								flex: 1
							}
						}}
					>
						Save
					</Button>
					<Button
						variant="filled"
						onClick={handleGeneratePDF}
						styles={{
							root: {
								backgroundColor: colors.buttonColor,
								color: colors.white,
								border: 'none',
								borderRadius: '8px',
								padding: '12px 20px',
								fontWeight: 'bold',
								minHeight: '48px',
								fontSize: fontSize.xl,
								flex: 1
							}
						}}
					>
						Print
					</Button>
					<Button
						variant="filled"
						onClick={handleCloseModal}
						styles={{
							root: {
								backgroundColor: colors.buttonColor,
								color: colors.white,
								border: 'none',
								borderRadius: '8px',
								padding: '12px 20px',
								fontWeight: 'bold',
								minHeight: '48px',
								fontSize: fontSize.xl,
								flex: 1
							}
						}}
					>
						Home
					</Button>
				</Group>
			</Box>

			{/* Modal Confirmation */}
			<Modal
				opened={showModal}
				onClose={() => setShowModal(false)}
				title="Design Saved!"
				centered
				styles={{
					modal: {
						backgroundColor: colors.white,
						borderRadius: '15px',
						padding: '20px',
						maxWidth: '90vw',
						width: '400px'
					},
					title: {
						fontSize: fontSize.xl,
						fontWeight: 'bold',
						color: colors.text,
						textAlign: 'center'
					},
					body: {
						padding: '20px 0'
					}
				}}
			>
				<Stack gap="20px" align="center">
					<Text style={{
						fontSize: fontSize.lg,
						color: colors.text,
						textAlign: 'center'
					}}>
						Your garden design has been saved!
					</Text>
					<Text style={{
						fontSize: fontSize.base,
						color: colors.text,
						textAlign: 'center',
            padding: '0 10vw'
					}}>
						You can now print a summary or return to the home page.
					</Text>
					<Button
						variant="filled"
						onClick={() => setShowModal(false)}
						styles={{
							root: {
								backgroundColor: colors.buttonColor,
								color: colors.white,
								border: 'none',
								borderRadius: '8px',
								padding: '10px 30px',
								fontWeight: 'bold',
								fontSize: fontSize.base
							}
						}}
					>
						OK
					</Button>
				</Stack>
			</Modal>
		</Box>
	);
};

export default SummaryTwoMobile;
